import { useEffect, useState } from "react";
import { events as evApi, floorPlans as fpApi } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, Star } from "lucide-react";

const EMPTY = {
  title: "", description: "", date: "", start_time: "22:00", end_time: "02:00",
  performer: "", flyer_url: "", ticket_url: "", reservation_enabled: true,
  ga_enabled: true, ga_price: 25, featured: false, category: "weekly", floor_plan_id: "",
};

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => evApi.list("all").then(setItems);
  useEffect(() => { load(); fpApi.list().then(setPlans); }, []);

  const start = (item) => { setEditing(item?.id || "new"); setForm(item ? { ...item } : EMPTY); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      if (editing === "new") await evApi.create(form);
      else await evApi.update(editing, form);
      toast.success("Saved");
      cancel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete event?")) return;
    await evApi.remove(id); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-events">
      <div className="flex justify-between items-center mb-8">
        <div>
          <span className="eyebrow">— Events</span>
          <h1 className="font-display text-4xl mt-2">Manage <span className="italic text-gold">Events</span></h1>
        </div>
        <button data-testid="add-event-btn" onClick={() => start(null)} className="btn-luxe"><Plus size={14} /> New Event</button>
      </div>

      {editing && (
        <div data-testid="event-editor" className="glass p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title"><input className="luxe-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Performer / DJ"><input className="luxe-input" value={form.performer} onChange={e => setForm({ ...form, performer: e.target.value })} /></Field>
            <Field label="Date"><input type="date" className="luxe-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Category">
              <select className="luxe-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                <option value="weekly">Weekly</option>
                <option value="special">Special</option>
                <option value="holiday">Holiday</option>
                <option value="celebrity">Celebrity</option>
                <option value="guest_dj">Guest DJ</option>
              </select>
            </Field>
            <Field label="Start Time"><input type="time" className="luxe-input" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></Field>
            <Field label="End Time"><input type="time" className="luxe-input" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></Field>
            <Field label="Flyer URL"><input className="luxe-input" value={form.flyer_url} onChange={e => setForm({ ...form, flyer_url: e.target.value })} /></Field>
            <Field label="Ticket URL"><input className="luxe-input" value={form.ticket_url} onChange={e => setForm({ ...form, ticket_url: e.target.value })} /></Field>
            <Field label="GA Price ($)"><input type="number" className="luxe-input" value={form.ga_price} onChange={e => setForm({ ...form, ga_price: parseFloat(e.target.value) || 0 })} /></Field>
            <Field label="Active Floor Plan">
              <select className="luxe-input" value={form.floor_plan_id || ""} onChange={e => setForm({ ...form, floor_plan_id: e.target.value || null })}>
                <option value="">— Default —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Description" full><textarea rows={3} className="luxe-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-4 text-sm">
            <label className="flex items-center gap-2 text-white/70"><input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
            <label className="flex items-center gap-2 text-white/70"><input type="checkbox" checked={form.reservation_enabled} onChange={e => setForm({ ...form, reservation_enabled: e.target.checked })} /> Reservations on</label>
            <label className="flex items-center gap-2 text-white/70"><input type="checkbox" checked={form.ga_enabled} onChange={e => setForm({ ...form, ga_enabled: e.target.checked })} /> GA tickets on</label>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} data-testid="save-event-btn" className="btn-luxe btn-luxe-solid"><Save size={14} /> Save</button>
            <button onClick={cancel} className="btn-luxe btn-luxe-ghost">Cancel</button>
          </div>
        </div>
      )}

      <div className="border border-white/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--onyx-charcoal)] text-[10px] tracking-luxe uppercase text-white/50">
            <tr><th className="text-left p-3">Title</th><th className="p-3">Date</th><th className="p-3">DJ</th><th className="p-3">GA</th><th className="p-3">★</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="p-3 font-display text-lg">{e.title}</td>
                <td className="p-3">{e.date}</td>
                <td className="p-3 text-white/60">{e.performer}</td>
                <td className="p-3 text-gold">${e.ga_price}</td>
                <td className="p-3">{e.featured && <Star size={14} className="text-gold fill-current" />}</td>
                <td className="p-3 text-right">
                  <button onClick={() => start(e)} className="text-[10px] tracking-luxe uppercase text-white/60 hover:text-gold mr-3">Edit</button>
                  <button onClick={() => remove(e.id)} className="text-red-400 hover:bg-red-500/10 p-1"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
